import React from 'react';

import {
  Autocomplete,
  AutocompleteProps,
  Box,
  Checkbox,
  ListItem,
  ListItemText,
  TextField,
  TextFieldProps,
} from '@mui/material';

interface IProps<T extends Record<string, any>, V extends string | number>
  extends Omit<
    AutocompleteProps<T, true, boolean, false>,
    'renderInput' | 'value'
  > {
  placeholder?: string;
  label?: string;
  getOptionLabel: (option: T) => string;
  getOptionValue: (option: T) => any;
  value: V[];
  textFieldProps?: TextFieldProps;
}

const SELECT_ALL_OPTION = { label: 'Select All', value: 'select_all' } as const;

type AutocompleteChangeHandler<T> = NonNullable<
  AutocompleteProps<T, true, false, false>['onChange']
>;

const AutocompleteMultipleOptions = <
  T extends Record<string, any>,
  V extends string | number,
>({
  placeholder,
  label,
  options,
  onChange,
  getOptionLabel,
  getOptionValue,
  value,
  sx,
  textFieldProps,
  ...autocompleteProps
}: IProps<T, V>) => {
  const getOptLabelWithSelectAll = (option: T) =>
    getOptionLabel(option) || option.label;

  const getOptionValueWithSelectAll = (option: T) =>
    getOptionValue(option) || option.value;

  const optionsWithSelectAll =
    options.length > 0 ? [SELECT_ALL_OPTION, ...options] : options;

  const handleChange: AutocompleteChangeHandler<T> = (
    e,
    newOptions,
    ...rest
  ) => {
    if (!onChange) return;

    if (
      newOptions.find(
        (opt) => getOptionValueWithSelectAll(opt) === 'select_all',
      )
    ) {
      onChange?.(
        e,
        options.map((opt) => ({ ...opt })),
        ...rest,
      );

      return;
    }

    onChange(e, newOptions, ...rest);
  };

  const selectedValues = value
    .map((v) => options.find((opt) => getOptionValue(opt) === v)!)
    .filter(Boolean);

  const isAllSelected = options.every((opt) =>
    value.includes(getOptionValue(opt)),
  );

  return (
    <Autocomplete
      getOptionLabel={getOptLabelWithSelectAll}
      value={selectedValues}
      sx={{
        '.MuiInputBase-root': {
          flexWrap: 'nowrap',
        },
        ...sx,
      }}
      onChange={handleChange}
      // Select all is just a placeholder option
      options={optionsWithSelectAll as unknown as T[]}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          size="small"
          placeholder={value?.length === 0 ? placeholder : undefined}
          {...textFieldProps}
        />
      )}
      freeSolo={false}
      multiple
      renderTags={(opts) => (
        <Box
          sx={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {opts.map((opt) => getOptionLabel(opt)).join(', ')}
        </Box>
      )}
      renderOption={(optProps, option, { selected }) => {
        return (
          <ListItem
            {...optProps}
            disableGutters
            disablePadding
            dense
            key={getOptionValueWithSelectAll(option)}
          >
            <Checkbox
              checked={
                getOptionValueWithSelectAll(option) === 'select_all'
                  ? isAllSelected
                  : selected
              }
              size="small"
            />
            <ListItemText
              primary={getOptLabelWithSelectAll(option)}
              slotProps={{
                primary: {
                  fontWeight:
                    getOptionValueWithSelectAll(option) === 'select_all'
                      ? 'bold'
                      : 'normal',
                },
              }}
            />
          </ListItem>
        );
      }}
      disableCloseOnSelect
      {...autocompleteProps}
    />
  );
};

export default AutocompleteMultipleOptions;
